<template>
  <SfTable
    :headers="headers"
    :rows="rows"
    :handles-sorting="false"
    :non-sortable-headers="nonSortableHeaders"
    :initial-sort-by="sortBy"
    :initial-sort-direction="sortDirection"
    :variant="tableVariants.DS"
    class="
      max-lg:[&_table]:tw-table-fixed max-lg:[&_table]:tw-w-full
      [&_tbody_tr:last-child_td]:tw-border-b-0
    "
    data-testid="groups-table"
    @table-sort="emit('sort', $event)"
  >
    <template #row="{ rowData, cellClasses }">
      <td
        v-for="column in GROUP_COLUMNS"
        :key="column"
        class="tw-text-sm"
        :class="[
          cellClasses,
          isEditable(rowData)
            ? 'tw-cursor-pointer tw-text-sf-slate-dark-1'
            : 'tw-text-sf-slate-dark-1/40',
        ]"
        :data-testid="`group-${rowData.id}-${column}`"
        @click="open(rowData)"
      >
        <div
          v-if="column === groupColumns.NAME"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span
            class="
              tw-flex
              tw-h-8
              tw-w-8
              tw-shrink-0
              tw-items-center
              tw-justify-center
              tw-rounded
            "
            :class="
              isEditable(rowData)
                ? 'tw-bg-sf-slate-subtle-2 tw-text-sf-slate-dark-1'
                : 'tw-bg-sf-slate-subtle-1 tw-text-sf-slate-dark-1/40'
            "
          >
            <i class="fa fa-user-group" aria-hidden="true" />
          </span>
          <SfTruncatedLabel
            :text="rowData.name"
            class="tw-font-medium tw-leading-5"
          />
          <SfStaticPill
            v-if="!isEditable(rowData)"
            v-tooltip="I18n.t('group.table.no_access_tooltip')"
            :variant="staticPillVariants.NEUTRAL"
            class="tw-shrink-0"
            data-testid="group-no-access"
          >
            <i class="fa fa-lock" aria-hidden="true" />
            {{ I18n.t('group.table.no_access') }}
          </SfStaticPill>
        </div>
        <template v-else-if="column === groupColumns.FILES">
          {{ rowData.filesCount }}
        </template>
        <template v-else-if="column === groupColumns.MEMBERS">
          {{ rowData.membersCount }}
        </template>
        <SfButtonDs
          v-else-if="column === groupColumns.ACTIONS"
          variant="tertiary"
          size="sm"
          icon-only
          icon-before="pen"
          :disabled="!isEditable(rowData)"
          :aria-label="I18n.t('group.edit_group')"
          @click.stop="open(rowData)"
        />
      </td>
    </template>
  </SfTable>
</template>

<script setup lang="ts">
import type {
  SfTableHeaderConfig,
  TableSortEvent,
} from '@app/shared/vue/components/sf-table/types'
import type {
  GroupListRow,
  GroupSortDirection,
  SortableGroupColumn,
} from '@app/application/user-management/types'

import { computed } from 'vue'

import {
  GROUP_COLUMNS,
  GROUP_COLUMN_CLASSES,
  GROUP_COLUMN_LABEL_KEYS,
} from '@app/application/user-management/constants'
import {
  groupColumns,
  groupSortDirections,
} from '@app/application/user-management/types'
import { isSortableGroupColumn } from '@app/application/user-management/predicates/is-sortable-group-column'
import { staticPillVariants } from '@app/shared/vue/components/sf-static-pill/constants'
import { tableVariants } from '@app/shared/vue/components/sf-table/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfButtonDs } from '@silverfin/ui-components'
import SfStaticPill from '@app/shared/vue/components/sf-static-pill/sf-static-pill.vue'
import SfTable from '@app/shared/vue/components/sf-table/index.vue'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'

defineOptions({ name: 'GroupsTable' })

interface Props {
  rows: GroupListRow[]
  sortBy?: SortableGroupColumn
  sortDirection?: GroupSortDirection
}

const {
  rows,
  sortBy = undefined,
  sortDirection = groupSortDirections.ASC,
} = defineProps<Props>()

const emit =
  defineEmits<{
    open: [groupId: number]
    sort: [event: TableSortEvent]
  }>()

const I18n = useI18n()

const nonSortableHeaders = computed(() =>
  GROUP_COLUMNS.filter((column) => !isSortableGroupColumn(column))
)

const headers = computed<SfTableHeaderConfig[]>(() =>
  GROUP_COLUMNS.map((column) => ({
    id: column,
    label: I18n.t(GROUP_COLUMN_LABEL_KEYS[column]),
    class: GROUP_COLUMN_CLASSES[column],
  }))
)

const isEditable = (group: GroupListRow): boolean => group.allowedActions.edit

const open = (group: GroupListRow): void => {
  if (!isEditable(group)) {
    return
  }

  emit('open', group.id)
}
</script>
